import {
  HOTBAR_SIZE,
  SPELL_CATALOGUE,
  type SpellTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { bindSpellTooltip, hideItemTooltip } from "./item-tooltip.js";
import { setupWindow, type WindowChromeHandle } from "./window-chrome.js";

export interface SpellbookCallbacks {
  // User dropped a spell on hotbar slot N. The server validates + persists.
  onAssignToHotbar: (slot: number, spellId: string) => void;
}

const DRAG_MIME = "application/x-vinyl-spellid";

export class SpellbookPanel {
  readonly root: HTMLElement;
  private readonly chrome: WindowChromeHandle;
  private knownIds: ReadonlySet<string> = new Set();

  constructor(private readonly cb: SpellbookCallbacks) {
    this.root = el("aside", { class: "spellbook-panel hidden" });
    this.chrome = setupWindow({
      windowEl: this.root,
      title: "Spellbook",
      storageKey: "spellbook",
    });
    this.render();
  }

  toggle(): void { this.chrome.toggle(); }

  setKnown(knownSpellIds: readonly string[]): void {
    this.knownIds = new Set(knownSpellIds);
    this.render();
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    hideItemTooltip();
    this.root.replaceChildren();
    this.root.append(
      this.chrome.header,
      el(
        "p",
        { class: "spellbook-hint" },
        `Drag a spell onto a hotbar slot (1–${HOTBAR_SIZE}). Locked spells require more stat points.`,
      ),
      this.renderArcaneSection(),
      this.renderDivineSection(),
    );
  }

  private renderArcaneSection(): HTMLElement {
    // M8 — "Arcane" tab now groups the 5 INT-driven schools (necro + 4 elements).
    return this.renderSection(
      "Arcane",
      SPELL_CATALOGUE.filter((s) => s.school !== "divine"),
    );
  }

  private renderDivineSection(): HTMLElement {
    return this.renderSection(
      "Divine",
      SPELL_CATALOGUE.filter((s) => s.school === "divine"),
    );
  }

  private renderSection(label: string, spells: readonly SpellTemplate[]): HTMLElement {
    const section = el("section", { class: "spellbook-section" });
    section.append(el("div", { class: "spellbook-section-label" }, label));
    for (const spell of spells) section.append(this.renderRow(spell));
    return section;
  }

  private renderRow(spell: SpellTemplate): HTMLElement {
    const known = this.knownIds.has(spell.id);
    const row = el(
      "div",
      {
        class: `spellbook-row school-${spell.school}${known ? "" : " locked"}`,
        draggable: known ? "true" : "false",
      },
      el("span", { class: "spellbook-row-name" }, spell.name),
      el("span", { class: "spellbook-row-meta" }, metaText(spell)),
    );
    bindSpellTooltip(row, spell, {
      known,
      footerHint: known ? "Drag onto a hotbar slot to bind." : undefined,
    });
    if (known) {
      row.addEventListener("dragstart", (ev) => {
        ev.dataTransfer?.setData(DRAG_MIME, spell.id);
        ev.dataTransfer?.setData("text/plain", spell.id);
        if (ev.dataTransfer) ev.dataTransfer.effectAllowed = "copy";
      });
    } else {
      row.append(el("span", { class: "spellbook-locked-badge" }, "🔒"));
    }
    return row;
  }
}

export { DRAG_MIME as SPELL_DRAG_MIME };

function metaText(spell: SpellTemplate): string {
  const parts: string[] = [];
  parts.push(`${spell.manaCost} mana`);
  parts.push(spell.castTimeMs === 0 ? "instant" : `${(spell.castTimeMs / 1000).toFixed(1)}s`);
  parts.push(`${(spell.cooldownMs / 1000).toFixed(1)}s CD`);
  if (spell.range > 0) parts.push(`range ${spell.range}`);
  return parts.join(" · ");
}

